import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, format } from 'date-fns';
import Calendar from '../../../../../../../../components/calendar';
import { DialogTimePicker } from '../../../../../../../../components/dialog-time-picker';
import { SelectOptionType } from '../../../../../../../../components/dialog/helper';
import { DialogSelect } from '../../../../../../../../components/dialog/select';
import { useTriggerFormContext } from '../../../../context/provider';
import { useOptions } from '../../../../hooks/price-condition/use-options';
import { UsePickersReturnType } from '../../../../hooks/price-condition/use-pickers';
import { RepeatCountDialog as CountDialog } from '../../../time-condition/components/repeat-count-dialog';
import { SelectedDaysWithHolidaysDialog } from '../../../time-condition/components/selected-days-with-holidays-dialog';
import { TimeEndDialog } from '../../../time-condition/components/time-end-dialog';
import { RepeatType } from '../../../time-condition/enums/repeat-type.enum';
import { TimeEndType } from '../../../time-condition/enums/time-end-type.enum';
import { PriceConditionType } from '../../enums';
import { PriceConditionDialog } from '../price-condition';
import { PriceDialog } from '../price-dialog';

type PriceConditionPickersProps = {
  pickers: UsePickersReturnType;
  priceConditionOptions: SelectOptionType<PriceConditionType>[];
  priceConditionTypes: PriceConditionType[];
  priceConditionTypeChange: (condition: PriceConditionType) => void;
  repeatSelected: SelectOptionType<RepeatType>;
  setRepeatSelected: Dispatch<SetStateAction<SelectOptionType<RepeatType>>>;
  timeEndType: SelectOptionType<TimeEndType>;
  setTimeEndType: Dispatch<SetStateAction<SelectOptionType<TimeEndType>>>;
};

export const PriceConditionPickers: React.FC<PriceConditionPickersProps> = ({
  pickers,
  priceConditionOptions,
  priceConditionTypes,
  priceConditionTypeChange,
  repeatSelected,
  setRepeatSelected,
  timeEndType,
  setTimeEndType,
}) => {
  const { t } = useTranslation('action');
  const { priceConditionConfig, setPriceConditionConfigValue } = useTriggerFormContext();
  const { repeatOptions, timeEndOptions } = useOptions();
  const {
    timePicker,
    timeEndPicker,
    repeatPicker,
    weekDaysPicker,
    repeatCountPicker,
    fromHourPicker,
    toHourPicker,
    priceTypeConditionPicker,
    isEndDateOpen,
    cheapestCountPicker,
    greaterPicker,
    lesserPicker,
  } = pickers;

  const timeEndOnChange = (optionIndex: number) => {
    setTimeEndType(timeEndOptions[optionIndex]);
    timeEndPicker.set(false);

    if (optionIndex === 1) isEndDateOpen.set(true);
    if (optionIndex === 2) {
      setPriceConditionConfigValue('endDate', new Date());
      repeatCountPicker.set(true);
    }
  };

  const repeatOnChange = (value) => {
    setRepeatSelected(repeatOptions.find((option) => option.value === value) ?? repeatOptions[0]);
    repeatPicker.set(false);

    switch (value) {
      case RepeatType.DAILY: {
        setTimeEndType(timeEndOptions[0]);
        break;
      }
      case RepeatType.SELECTED: {
        weekDaysPicker.set(true);
        break;
      }
      default: {
        break;
      }
    }

    if (value !== RepeatType.SELECTED) setPriceConditionConfigValue('selectedDays', []);
  };

  const renderOptionDetails = (val: PriceConditionType) => {
    switch (val) {
      case PriceConditionType.HOURS_FROM_TO:
        return priceConditionConfig.betweenHoursFromInclusive != null &&
          priceConditionConfig.betweenHoursToInclusive != null ? (
          <span>
            : {`${priceConditionConfig.betweenHoursFromInclusive}-${priceConditionConfig.betweenHoursToInclusive}`}
          </span>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <>
      <DialogTimePicker
        header={t('hour')}
        open={timePicker.open}
        setOpen={timePicker.set}
        onSave={(value) => setPriceConditionConfigValue('timeRepeat', value)}
        time={priceConditionConfig.timeRepeat}
      />

      <Calendar
        hidden
        date={priceConditionConfig.endDate}
        label={''}
        onChange={(date) => {
          isEndDateOpen.set(false);
          setPriceConditionConfigValue('endDate', date);
        }}
        open={isEndDateOpen.open}
        onOpen={() => isEndDateOpen.set(true)}
        onClose={() => isEndDateOpen.set(false)}
        minDate={addDays(priceConditionConfig.startDate, 1)}
      />

      <DialogSelect
        show={repeatPicker.open}
        setShow={repeatPicker.set}
        title={t('trigger.conditions.time.repeating')}
        options={repeatOptions}
        value={repeatOptions.find((option) => option.value === repeatSelected.value)?.value}
        onChange={repeatOnChange}
      />

      <SelectedDaysWithHolidaysDialog
        show={weekDaysPicker.open}
        setShow={weekDaysPicker.set}
        selected={priceConditionConfig.selectedDays}
        holidays={priceConditionConfig.holidaysHandling}
        onSave={(values, holidays) => {
          setPriceConditionConfigValue('selectedDays', values);
          setPriceConditionConfigValue('holidaysHandling', holidays);
        }}
        setShowRepeatPicker={repeatPicker.set}
      />

      <TimeEndDialog
        show={timeEndPicker.open}
        setShow={timeEndPicker.set}
        title={t('trigger.conditions.end')}
        options={timeEndOptions}
        selectedOptionIndex={timeEndOptions.findIndex((option) => option.value === timeEndType.value)}
        onChange={(index) => timeEndOnChange(index)}
        count={priceConditionConfig.repeatCount}
        date={format(priceConditionConfig.endDate, 'dd.MM.yyyy')}
      />

      <CountDialog
        open={repeatCountPicker.open}
        setOpen={repeatCountPicker.set}
        title={t('trigger.timeEndOptions.count')}
        onSave={(value) => setPriceConditionConfigValue('repeatCount', value)}
        count={priceConditionConfig.repeatCount}
      />

      <PriceConditionDialog
        showPriceTypeConditionPicker={priceTypeConditionPicker.open}
        handleClose={() => priceTypeConditionPicker.set(null)}
        title={t('trigger.conditions.priceCondition')}
        options={priceConditionOptions}
        selectedOptionIndex={priceConditionOptions.findIndex(
          (option) => option.value === priceTypeConditionPicker.open,
        )}
        onChange={(condition) => priceConditionTypeChange(condition)}
        renderOptionDetails={renderOptionDetails}
        priceConditionTypes={priceConditionTypes}
      />

      <DialogTimePicker
        header={t('trigger.priceConditionOptions.from')}
        open={fromHourPicker.open}
        setOpen={fromHourPicker.set}
        onSave={(value) => {
          setPriceConditionConfigValue('betweenHoursFromInclusive', value);
          fromHourPicker.set(false);
          toHourPicker.set(true);
        }}
        time={priceConditionConfig.betweenHoursFromInclusive}
        maxHours={22}
        onlyFullMinutes
      />

      <DialogTimePicker
        header={t('trigger.priceConditionOptions.to')}
        open={toHourPicker.open}
        setOpen={toHourPicker.set}
        onSave={(value) => {
          setPriceConditionConfigValue('betweenHoursToInclusive', value);
          toHourPicker.set(false);
        }}
        time={priceConditionConfig.betweenHoursToInclusive}
        onlyFullMinutes
        minHours={Number(priceConditionConfig.betweenHoursFromInclusive.split(':')[0]) + 1}
        maxHours={23}
      />

      <CountDialog
        open={cheapestCountPicker.open}
        setOpen={cheapestCountPicker.set}
        title={t('trigger.priceConditionOptions.cheapestPeriods')}
        onSave={(value) => setPriceConditionConfigValue('numberOfCheapestPeriodsInDay', value)}
        count={priceConditionConfig.numberOfCheapestPeriodsInDay}
        maxCount={24}
      />

      <PriceDialog
        show={greaterPicker.open}
        setShow={greaterPicker.set}
        price={priceConditionConfig.priceGreaterThanOrEqual}
        onSave={(value) => setPriceConditionConfigValue('priceGreaterThanOrEqual', value)}
        label={`${t('trigger.priceConditionOptions.priceGreater')} (zł/kWh)`}
      />

      <PriceDialog
        show={lesserPicker.open}
        setShow={lesserPicker.set}
        price={priceConditionConfig.priceLesserThanOrEqual}
        onSave={(value) => setPriceConditionConfigValue('priceLesserThanOrEqual', value)}
        label={`${t('trigger.priceConditionOptions.priceLesser')} (zł/kWh)`}
      />
    </>
  );
};
